import { includes, get } from "lodash"
import { GMV, REFERENCE_EQUITY } from "./constants"
import type { ReactNode } from "react"

export type MetricId = string
export type MetricValueGetter = (data: unknown) => number | null
export type MetricFormatter = (value: number | null) => ReactNode

interface BaseMetric {
  readonly label: string
  readonly formatter?: MetricFormatter
}

// Single-date pattern: uses valueGetter function to extract values from GraphQL results
interface MetricWithValueGetter extends BaseMetric {
  readonly valueGetter: MetricValueGetter
}

// Multi-date pattern: uses path string to extract values from flattened data
interface MetricWithPath extends BaseMetric {
  readonly path: string
}

// A metric can have valueGetter, path, or both (for multi-date long/short metrics)
export type Metric = MetricWithValueGetter | MetricWithPath
export type MetricsMap = Readonly<Record<MetricId, Metric>>

export function shouldExcludeMetric(
  metricId: MetricId,
  noGmvOrEquity: boolean,
  hasReferenceEquity: boolean,
): boolean {
  if (noGmvOrEquity && includes([GMV, REFERENCE_EQUITY], metricId)) {
    return true
  }

  if (metricId === REFERENCE_EQUITY && !hasReferenceEquity) {
    return true
  }

  return false
}

// Retrieves metric value, handling both single-date (valueGetter) and multi-date (path) patterns
export function getMetricValue(
  metricId: MetricId,
  allMetrics: MetricsMap,
  data: object,
  noGmvOrEquity: boolean,
  hasReferenceEquity: boolean,
): number | null {
  if (shouldExcludeMetric(metricId, noGmvOrEquity, hasReferenceEquity)) {
    return null
  }

  const metric = allMetrics[metricId]

  if (!metric) {
    return null
  }

  if ("path" in metric) {
    return get(data, metric.path, null)
  }

  if ("valueGetter" in metric) {
    return metric.valueGetter(data)
  }

  return null
}
